/**
 * Pure transport math shared by the lookahead scheduler and the playhead display.
 * Kept free of React/Web Audio so the loop/wrap behavior is unit-testable.
 */

export const MIN_LOOP_LENGTH_BEATS = 0.25;

/**
 * Resolve the active loop region.
 * - Explicit loop (loopEnabled) wins when it is long enough to walk safely.
 * - Otherwise the implicit whole-song loop: restart at the end of the last clip.
 * - Returns null for free run (no loop).
 */
export function resolveLoopRegion({ loopEnabled, loopStartBeat, loopEndBeat, clips }) {
  if (loopEnabled) {
    if (Number.isFinite(loopStartBeat) && Number.isFinite(loopEndBeat)
        && loopEndBeat > loopStartBeat + MIN_LOOP_LENGTH_BEATS - 1e-9) {
      return { startBeat: Math.max(0, loopStartBeat), endBeat: loopEndBeat };
    }
    // Degenerate explicit loop: fall back to free run rather than starving the
    // scheduler walk with a near-zero-length region.
    return null;
  }

  if (Array.isArray(clips) && clips.length > 0) {
    const lastBeat = Math.max(...clips.map(c => (c?.startBeat || 0) + (c?.durationBeats || 0)));
    if (lastBeat > MIN_LOOP_LENGTH_BEATS) {
      return { startBeat: 0, endBeat: lastBeat };
    }
  }
  return null;
}

/**
 * Map an unwrapped transport beat (start beat + elapsed beats) onto the timeline,
 * mirroring exactly how the scheduler wraps at the loop boundary:
 * beats below the loop end pass through; at/after it, wrap modulo the region.
 */
export function wrapTransportBeat(unwrapped, region) {
  if (!region || unwrapped < region.endBeat) return unwrapped;
  const length = region.endBeat - region.startBeat;
  if (length <= 1e-6) return region.startBeat;
  return region.startBeat + ((unwrapped - region.endBeat) % length);
}

/** Clamp a note velocity to the engine's 0..1 gain domain (default full). */
export function clampVelocity(value, fallback = 1) {
  // Number(null) === 0 would silence legacy events with no velocity — treat
  // absent values as the fallback, not as zero.
  if (value === null || value === undefined || value === '') return fallback;
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(0, Math.min(1, num));
}

/**
 * Convert a moment on the performance.now() timeline (where DOM and Web MIDI
 * event timestamps live) into the AudioContext clock.
 *
 * The two clocks are unrelated, so they must be related through a sample of
 * both taken together — exactly what AudioContext.getOutputTimestamp() returns.
 * Returns null when either input is unusable, so callers can fall back rather
 * than record a note at a nonsense time.
 */
export function contextTimeAtPerformanceTime(performanceTime, outputTimestamp) {
  const sampledContext = Number(outputTimestamp?.contextTime);
  const sampledPerformance = Number(outputTimestamp?.performanceTime);
  const target = Number(performanceTime);
  if (!Number.isFinite(sampledContext) || !Number.isFinite(sampledPerformance)) return null;
  if (!Number.isFinite(target)) return null;
  return sampledContext + ((target - sampledPerformance) / 1000);
}

/**
 * Unwrapped transport beat for a moment on the audio clock.
 *
 * Recording reads this instead of the shared playhead clock, which is only
 * written once per animation frame and therefore quantizes captured notes to
 * ~17ms — about a 1/29th note at 120bpm, and audible.
 *
 * `latency` is the output latency. Applying it is deliberate: a performer plays
 * along to what they HEAR, so the note belongs where the sound was, not where
 * the engine was processing. This is standard record-latency compensation and
 * keeps captured notes aligned with the on-screen playhead.
 */
export function transportBeatAtContextTime({ contextTime, startTime, startBeat, bps, latency = 0 }) {
  const values = [contextTime, startTime, startBeat, bps];
  // Number(null) === 0, so a stopped transport (playbackStartTime === null)
  // would otherwise pass a finite check and yield a bogus beat.
  const usable = (value) => value !== null && value !== undefined && value !== ''
    && Number.isFinite(Number(value));
  if (!values.every(usable)) return null;
  if (Number(bps) <= 0) return null;
  const elapsed = Math.max(0, Number(contextTime) - Number(latency || 0) - Number(startTime));
  return Number(startBeat) + (elapsed * Number(bps));
}

/**
 * Split a lookahead window into segments that respect the loop boundary.
 *
 * A scheduler that only walks forward in unwrapped beats never schedules
 * across a loop: approaching the end its window runs past the loop point into
 * beats that will not play, and after the wrap the notes at the loop start are
 * already behind it. That is why the first notes of a loop dropped out at
 * random — whether they survived depended on where the frame landed relative
 * to the wrap.
 *
 * Returns segments in wrapped (playable) beat space. `beatOffset` is how many
 * beats after `fromBeat` the segment begins, which is what converts a segment
 * position into an audio time.
 */
export function scheduleSegments({ fromBeat, lookaheadBeats, region = null, maxSegments = 8 }) {
  const start = Number(fromBeat);
  const span = Number(lookaheadBeats);
  if (!Number.isFinite(start) || !Number.isFinite(span) || span <= 0) return [];

  const loopLength = region ? region.endBeat - region.startBeat : 0;
  if (!region || loopLength <= 1e-6) {
    return [{ fromBeat: start, toBeat: start + span, beatOffset: 0 }];
  }

  const segments = [];
  let cursor = wrapTransportBeat(start, region);
  // A position before the loop starts is not inside it yet; play up to the
  // loop entry without wrapping early.
  if (cursor < region.startBeat) cursor = start;
  let remaining = span;
  let offset = 0;

  while (remaining > 1e-9 && segments.length < maxSegments) {
    const roomToEnd = cursor < region.startBeat
      ? Math.max(0, region.startBeat - cursor) + loopLength
      : region.endBeat - cursor;
    const length = Math.min(remaining, roomToEnd);
    if (length <= 1e-9) break;
    segments.push({ fromBeat: cursor, toBeat: cursor + length, beatOffset: offset });
    remaining -= length;
    offset += length;
    cursor += length;
    if (cursor >= region.endBeat - 1e-9) cursor = region.startBeat;
  }
  return segments;
}

/**
 * Build a scheduler window from the transport's audio-clock anchor.
 *
 * React effects necessarily run after the transport start has been committed.
 * On their first pass, even a few milliseconds of elapsed audio time puts a
 * note at the start beat behind an ordinary forward-only window. `includeStart`
 * catches up that small startup interval while `catchupBeats` lets consumers
 * keep every event at its intended time relative to the live audio clock.
 *
 * Catch-up is deliberately bounded. Resuming a suspended/backgrounded tab
 * must not dump an arbitrarily long backlog of MIDI into the instrument.
 */
export function scheduleClockWindow({
  startBeat,
  elapsedSeconds,
  bps,
  lookaheadSeconds,
  region = null,
  includeStart = false,
  maxCatchupSeconds = 0.25
}) {
  const safeStart = Number(startBeat);
  const safeElapsed = Math.max(0, Number(elapsedSeconds));
  const safeBps = Number(bps);
  const safeLookahead = Number(lookaheadSeconds);
  if (![safeStart, safeElapsed, safeBps, safeLookahead].every(Number.isFinite)
    || safeBps <= 0 || safeLookahead <= 0) return null;

  const unwrappedBeat = safeStart + (safeElapsed * safeBps);
  const catchupSeconds = includeStart
    ? Math.min(safeElapsed, Math.max(0, Number(maxCatchupSeconds) || 0))
    : 0;
  const catchupBeats = catchupSeconds * safeBps;
  const segments = scheduleSegments({
    fromBeat: unwrappedBeat - catchupBeats,
    lookaheadBeats: (safeLookahead + catchupSeconds) * safeBps,
    region
  });

  return { unwrappedBeat, catchupBeats, segments };
}

/** Presentation time of the audio leaving the device, not its processing clock.
 * Output timestamps already include device latency: never subtract it twice.
 * Reject the zero timestamp before the first block and stale samples on resume.
 */
export function audibleContextTime(context, performanceTime) {
  const now = Number(context.currentTime);
  try {
    const sample = context.getOutputTimestamp?.();
    const age = performanceTime - Number(sample?.performanceTime);
    if (Number(sample?.performanceTime) > 0 && Number(sample?.contextTime) >= 0
      && Number.isFinite(age) && age >= -50 && age < 1000) {
      const presented = contextTimeAtPerformanceTime(performanceTime, sample);
      if (Number.isFinite(presented) && presented >= 0 && presented <= now) return presented;
    }
  } catch (_) { /* Some embedded browsers expose the method but cannot sample it. */ }
  // These cover separate stages: graph-to-subsystem and subsystem-to-device.
  return Math.max(0, now - Math.max(0, Number(context.baseLatency) || 0)
    - Math.max(0, Number(context.outputLatency) || 0));
}
